import * as React from 'react';

import { Button, Collapse, CollapsePanelProps, Space } from 'antd';
import { CloseOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';

import { InterviewComponent } from 'src/components/interviewComponents/types/interviewComponent';
import { InterviewStepHandle } from 'src/components/interviewComponents/types/interviewStepHandle';
import { useTranslation } from 'react-i18next';

interface EditableCollapsePanelProps extends CollapsePanelProps {
    editInterviewComponent: InterviewComponent<unknown>;

    onEditStateChanged?: (isEditing: boolean) => void;
}

export const EditableCollapsePanel: React.FC<EditableCollapsePanelProps> = (props) => {
    const { t } = useTranslation();

    const {
        // Alias 'editInterviewComponent' prop to 'EditInterviewComponent' - Must be capitalized to use in JSX below
        editInterviewComponent: EditInterviewComponent,
        onEditStateChanged,
        ...panelProps
    } = props;

    const [isEditing, setIsEditing] = React.useState(false);

    const interviewComponentRef = React.useRef<InterviewStepHandle>(null);

    const handleEditChange = (isEditing: boolean): void => {
        setIsEditing(isEditing);

        onEditStateChanged?.(isEditing);
    };

    return (
        <Collapse.Panel
            {...panelProps}
            extra={
                isEditing
                    ?
                    <Space>
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={(e) => {
                                // Prevent the panel itself from collapsing/expanding when the button is clicked
                                e.stopPropagation();

                                handleEditChange(false);
                            }}
                        >
                            {t('Common.cancel')}
                        </Button>

                        <Button
                            size="small"
                            icon={<SaveOutlined />}
                            type="primary"
                            onClick={async (e) => {
                                // Prevent the panel itself from collapsing/expanding when the button is clicked
                                e.stopPropagation();

                                const isSubmitted = await interviewComponentRef.current?.submit();

                                if (isSubmitted) {
                                    handleEditChange(false);
                                }
                            }}
                        >
                            {t('Common.save')}
                        </Button>
                    </Space>
                    :
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        type="primary"
                        onClick={(e) => {
                            // Prevent the panel itself from collapsing/expanding when the button is clicked
                            e.stopPropagation();

                            handleEditChange(true);
                        }}
                    >
                        {t('Common.edit')}
                    </Button>
            }
        >
            {isEditing
                ?
                // We're editing - show the InterviewComponent (form) passed in 
                <EditInterviewComponent
                    ref={interviewComponentRef}
                />
                :
                // We're not editing - just show the content given to the panel
                props.children
            }
        </Collapse.Panel>
    );
};